// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableSelectAll,
  TableSelectRow,
  Button,
  Modal,
  TextInput,
  Form,
  FormGroup,
  Stack,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  Tag,
  Tile,
  Column,
  Grid,
  FileUploader,
  InlineLoading,
  Heading,
  Breadcrumb,
  BreadcrumbItem,
  DatePicker,
  DatePickerInput,
} from '@carbon/react';
import { Add, Download, Upload, User, Email, Education, CheckmarkFilled, WarningFilled } from '@carbon/icons-react';
import { useNavigate, useParams } from 'react-router-dom';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  grade: string;
  parentEmail?: string;
  dateOfBirth?: string;
  status: 'pending' | 'active' | 'invited' | 'requires-parent-consent';
  coppaCompliant?: boolean;
  parentConsentReceived?: boolean;
  createdAt?: string;
}

interface SchoolInfo {
  id: string;
  name: string;
  adminEmail: string;
  studentCount: number;
  maxStudents: number;
}

const StudentBulkCreation: React.FC = () => {
  const navigate = useNavigate();
  const { schoolId } = useParams<{ schoolId: string }>();
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newStudent, setNewStudent] = useState<Partial<Student>>({
    firstName: '',
    lastName: '',
    email: '',
    grade: '',
    parentEmail: '',
    dateOfBirth: '',
  });

  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://staging.sigma08.com';

  // COPPA compliance helper function
  const calculateAge = (dateOfBirth: string): number => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const checkCoppaCompliance = (student: Partial<Student>): boolean => {
    if (!student.dateOfBirth) return false;
    const age = calculateAge(student.dateOfBirth);
    return age < 13 ? !!student.parentEmail : true;
  };

  const getStudentStatus = (student: Partial<Student>): Student['status'] => {
    if (!student.dateOfBirth) return 'pending';

    const age = calculateAge(student.dateOfBirth);
    if (age < 13) {
      return student.parentEmail ? 'requires-parent-consent' : 'pending';
    }

    return 'pending';
  };

  useEffect(() => {
    verifySchoolAdmin();
    fetchSchoolInfo();
  }, [schoolId]);

  const verifySchoolAdmin = async () => {
    try {
      const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
      if (!token) {
        navigate('/login?redirect=/register/school/' + schoolId + '/students');
        return;
      }

      const response = await fetch(`${apiUrl}/api/v1/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      if (data.role !== 'school_admin' || data.schoolId !== schoolId) {
        setError('You must be a school administrator to access this page');
        setTimeout(() => navigate('/'), 3000);
      }
    } catch (err) {
      console.error('Authentication error:', err);
      navigate('/login?redirect=/register/school/' + schoolId + '/students');
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/v1/schools/${schoolId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSchoolInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch school info:', err);
    }
  };

  const handleAddStudent = () => {
    setIsModalOpen(true);
    setNewStudent({
      firstName: '',
      lastName: '',
      email: '',
      grade: '',
      parentEmail: '',
      dateOfBirth: '',
    });
  };

  const handleSaveStudent = () => {
    if (!newStudent.firstName || !newStudent.lastName || !newStudent.email) {
      setError('Please fill in all required fields');
      return;
    }

    // COPPA compliance validation
    if (newStudent.dateOfBirth) {
      const age = calculateAge(newStudent.dateOfBirth);
      if (age < 13 && !newStudent.parentEmail) {
        setError('Parent email is required for students under 13 (COPPA compliance)');
        return;
      }
    }

    const student: Student = {
      id: `temp-${Date.now()}`,
      firstName: newStudent.firstName || '',
      lastName: newStudent.lastName || '',
      email: newStudent.email || '',
      grade: newStudent.grade || '',
      parentEmail: newStudent.parentEmail,
      dateOfBirth: newStudent.dateOfBirth,
      status: getStudentStatus(newStudent),
      coppaCompliant: checkCoppaCompliance(newStudent),
    };

    setStudents([...students, student]);
    setIsModalOpen(false);
    setSuccess(
      `Student added to batch${student.status === 'requires-parent-consent' ? ' (Parent consent required)' : ''}`
    );
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleBulkUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim() !== '');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

      // Expected headers: firstName, lastName, email, grade, parentEmail, dateOfBirth
      const requiredHeaders = ['firstname', 'lastname', 'email', 'grade'];
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

      if (missingHeaders.length > 0) {
        setError(`Missing required columns: ${missingHeaders.join(', ')}`);
        setLoading(false);
        return;
      }

      const uploadedStudents: Student[] = [];
      const warnings: string[] = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        if (values.length >= 4) {
          const studentData = {
            firstName: values[headers.indexOf('firstname')],
            lastName: values[headers.indexOf('lastname')],
            email: values[headers.indexOf('email')],
            grade: values[headers.indexOf('grade')],
            parentEmail: values[headers.indexOf('parentemail')] || undefined,
            dateOfBirth: values[headers.indexOf('dateofbirth')] || undefined,
          };

          // COPPA compliance check
          if (studentData.dateOfBirth) {
            const age = calculateAge(studentData.dateOfBirth);
            if (age < 13 && !studentData.parentEmail) {
              warnings.push(
                `Row ${i + 1}: ${studentData.firstName} ${studentData.lastName} is under 13 but no parent email provided`
              );
            }
          }

          const student: Student = {
            id: `temp-${Date.now()}-${i}`,
            firstName: studentData.firstName,
            lastName: studentData.lastName,
            email: studentData.email,
            grade: studentData.grade,
            parentEmail: studentData.parentEmail,
            dateOfBirth: studentData.dateOfBirth,
            status: getStudentStatus(studentData),
            coppaCompliant: checkCoppaCompliance(studentData),
          };

          uploadedStudents.push(student);
        }
      }

      setStudents([...students, ...uploadedStudents]);

      let message = `${uploadedStudents.length} students added from CSV`;
      const coppaRequired = uploadedStudents.filter(s => s.status === 'requires-parent-consent').length;
      if (coppaRequired > 0) {
        message += `. ${coppaRequired} students require parent consent (COPPA compliance)`;
      }

      if (warnings.length > 0) {
        setError(`Warnings found:\n${warnings.join('\n')}`);
      } else {
        setSuccess(message);
      }
    } catch (err) {
      setError('Failed to parse CSV file. Please check the format.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccounts = async () => {
    if (students.length === 0) {
      setError('Please add at least one student');
      return;
    }

    setLoading(true);
    setCurrentStep(1);

    try {
      const response = await fetch(`${apiUrl}/api/v1/accounts/school/students`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          schoolId,
          students: students.map(s => ({
            firstName: s.firstName,
            lastName: s.lastName,
            email: s.email,
            grade: s.grade,
            parentEmail: s.parentEmail,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create student accounts');
      }

      const data = await response.json();
      setCurrentStep(2);
      setSuccess(`Successfully created ${data.created} student accounts`);

      setTimeout(() => {
        navigate('/school/dashboard');
      }, 3000);
    } catch (err) {
      setError('Failed to create student accounts. Please try again.');
      setCurrentStep(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = `FirstName,LastName,Email,Grade,ParentEmail,DateOfBirth
John,Doe,john.doe@school.edu,9,parent@email.com,2005-06-15
Jane,Smith,jane.smith@school.edu,6,parent2@email.com,2011-03-22
Alice,Johnson,alice.johnson@school.edu,12,parent3@email.com,2009-09-10`;
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_template.csv';
    a.click();
  };

  const progressSteps = [
    { label: 'Add Students', description: 'Add student information' },
    { label: 'Create Accounts', description: 'Processing accounts' },
    { label: 'Complete', description: 'Accounts created' },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--cds-background)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <Stack gap={7}>
          {/* Breadcrumb */}
          <Breadcrumb>
            <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
            <BreadcrumbItem href="/school/admin">School Admin</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>Create Student Accounts</BreadcrumbItem>
          </Breadcrumb>

          {/* Header */}
          <div>
            <Heading>Create Student Accounts</Heading>
            {schoolInfo && (
              <p style={{ marginTop: '0.5rem', color: 'var(--cds-text-secondary)' }}>
                {schoolInfo.name} • {schoolInfo.studentCount}/{schoolInfo.maxStudents} students
              </p>
            )}
          </div>

          {/* Progress Indicator */}
          <ProgressIndicator currentIndex={currentStep} spaceEqually>
            {progressSteps.map((step, index) => (
              <ProgressStep
                key={index}
                label={step.label}
                description={step.description}
                complete={currentStep > index}
                current={currentStep === index}
              />
            ))}
          </ProgressIndicator>

          {/* Notifications */}
          {error && <InlineNotification kind="error" title="Error" subtitle={error} onClose={() => setError(null)} />}
          {success && (
            <InlineNotification kind="success" title="Success" subtitle={success} onClose={() => setSuccess(null)} />
          )}

          {/* Action Buttons */}
          <Grid>
            <Column lg={8} md={8} sm={4}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <Button renderIcon={Add} onClick={handleAddStudent} disabled={loading}>
                  Add Student
                </Button>
                <FileUploader
                  accept={['.csv']}
                  buttonLabel="Upload CSV"
                  filenameStatus="edit"
                  iconDescription="Upload CSV file"
                  labelDescription="Upload a CSV file with student information"
                  labelTitle=""
                  onChange={handleBulkUpload}
                  disabled={loading}
                />
                <Button kind="ghost" renderIcon={Download} onClick={handleDownloadTemplate}>
                  Download Template
                </Button>
              </div>
            </Column>
            <Column lg={8} md={0} sm={0} />
          </Grid>

          {/* Students Table */}
          {students.length > 0 && (
            <DataTable
              rows={students}
              headers={[
                { key: 'firstName', header: 'First Name' },
                { key: 'lastName', header: 'Last Name' },
                { key: 'email', header: 'Email' },
                { key: 'grade', header: 'Grade' },
                { key: 'dateOfBirth', header: 'Date of Birth' },
                { key: 'parentEmail', header: 'Parent Email' },
                { key: 'status', header: 'Status' },
              ]}
            >
              {({ rows, headers, getHeaderProps, getRowProps, getSelectionProps, getTableProps }: any) => (
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      <TableSelectAll {...getSelectionProps()} />
                      {headers.map((header: any) => (
                        <TableHeader {...getHeaderProps({ header })} key={header.key}>
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row: any) => (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        <TableSelectRow {...getSelectionProps({ row })} />
                        {row.cells.map((cell: any) => (
                          <TableCell key={cell.id}>
                            {cell.info.header === 'status' ? (
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <Tag
                                  type={
                                    cell.value === 'active'
                                      ? 'green'
                                      : cell.value === 'requires-parent-consent'
                                        ? 'purple'
                                        : cell.value === 'invited'
                                          ? 'blue'
                                          : 'gray'
                                  }
                                  size="sm"
                                >
                                  {cell.value === 'requires-parent-consent' ? 'COPPA Required' : cell.value}
                                </Tag>
                                {cell.value === 'requires-parent-consent' && (
                                  <WarningFilled size={16} style={{ color: 'var(--cds-support-warning)' }} />
                                )}
                              </div>
                            ) : cell.info.header === 'dateOfBirth' ? (
                              cell.value ? (
                                new Date(cell.value).toLocaleDateString()
                              ) : (
                                '-'
                              )
                            ) : (
                              cell.value || '-'
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DataTable>
          )}

          {/* Create Accounts Button */}
          {students.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                kind="primary"
                renderIcon={loading ? undefined : CheckmarkFilled}
                onClick={handleCreateAccounts}
                disabled={loading}
              >
                {loading ? <InlineLoading description="Creating accounts..." /> : `Create ${students.length} Accounts`}
              </Button>
            </div>
          )}

          {/* Help Section */}
          <Tile>
            <Stack gap={3}>
              <Heading size="compact-01">Instructions</Heading>
              <p style={{ fontSize: '14px' }}>
                • Add students individually or upload a CSV file with student information
                <br />• <strong>COPPA Compliance:</strong> Students under 13 require parent email for consent
                <br />
                • CSV format: FirstName, LastName, Email, Grade, ParentEmail, DateOfBirth
                <br />
                • Parents will receive email invitations to complete account setup for minors
                <br />
                • Student accounts will be created with temporary passwords
                <br />• Students can log in using their school email once accounts are activated
              </p>
            </Stack>
          </Tile>
        </Stack>
      </div>

      {/* Add Student Modal */}
      <Modal
        open={isModalOpen}
        modalHeading="Add Student"
        primaryButtonText="Add"
        secondaryButtonText="Cancel"
        onRequestClose={() => setIsModalOpen(false)}
        onRequestSubmit={handleSaveStudent}
      >
        <Form>
          <Stack gap={5}>
            <FormGroup legendText="Student Information">
              <TextInput
                id="firstName"
                labelText="First Name"
                value={newStudent.firstName || ''}
                onChange={e => setNewStudent({ ...newStudent, firstName: e.target.value })}
                required
              />
              <TextInput
                id="lastName"
                labelText="Last Name"
                value={newStudent.lastName || ''}
                onChange={e => setNewStudent({ ...newStudent, lastName: e.target.value })}
                required
                style={{ marginTop: '1rem' }}
              />
              <TextInput
                id="email"
                labelText="Student Email"
                value={newStudent.email || ''}
                onChange={e => setNewStudent({ ...newStudent, email: e.target.value })}
                required
                style={{ marginTop: '1rem' }}
              />
              <TextInput
                id="grade"
                labelText="Grade Level"
                value={newStudent.grade || ''}
                onChange={e => setNewStudent({ ...newStudent, grade: e.target.value })}
                style={{ marginTop: '1rem' }}
              />
              <DatePicker datePickerType="single" style={{ marginTop: '1rem' }}>
                <DatePickerInput
                  id="dateOfBirth"
                  labelText="Date of Birth (for COPPA compliance)"
                  placeholder="mm/dd/yyyy"
                  value={newStudent.dateOfBirth || ''}
                  onChange={e => setNewStudent({ ...newStudent, dateOfBirth: e.target.value })}
                  helperText="Used to determine if parental consent is required"
                />
              </DatePicker>
              <TextInput
                id="parentEmail"
                labelText="Parent Email (optional)"
                value={newStudent.parentEmail || ''}
                onChange={e => setNewStudent({ ...newStudent, parentEmail: e.target.value })}
                helperText="Required for students under 13 (COPPA compliance)"
                style={{ marginTop: '1rem' }}
              />
            </FormGroup>
          </Stack>
        </Form>
      </Modal>
    </div>
  );
};

export default StudentBulkCreation;
